import type en from '../en/auth'
import type { Translation } from '../types'

/** Signing in, passwords, two-factor sign-in and the sessions of an account */
const auth: Translation<typeof en> = {
  "auth.tagline": "Plateforme de gestion de réseau",
  "auth.ssoCouldNotStart": "L’authentification unique n’a pas pu démarrer",
  "auth.loginFailed": "Échec de la connexion",
  "auth.idleSignedOut": "Vous avez été déconnecté après une période d’inactivité. Reconnectez-vous pour continuer.",
  "auth.emailPlaceholder": "vous@entreprise.fr",
  "auth.signIn": "Se connecter",
  "auth.signInSso": "Se connecter avec l’authentification unique",
  "auth.ssoByEmailHint": "Votre adresse professionnelle indique la connexion de quelle entreprise utiliser.",
  "auth.continueSso": "Continuer avec l’authentification unique",
  "auth.forgotPassword": "Mot de passe oublié ?",
  "auth.resetFailed": "Impossible d’envoyer l’e-mail de réinitialisation",
  "auth.setFailed": "Impossible de définir le mot de passe",
  "auth.resetTitle": "Réinitialiser le mot de passe",
  "auth.setTitle": "Définissez votre mot de passe",
  "auth.resetSubtitle": "Saisissez votre adresse e-mail pour recevoir un lien de réinitialisation",
  "auth.setSubtitle": "Créez un mot de passe sécurisé pour votre compte",
  "auth.checkEmail": "Consultez vos e-mails",
  "auth.passwordSet": "Mot de passe défini",
  "auth.resetSent": "Si cette adresse existe, un lien de réinitialisation a été envoyé.",
  "auth.accountActive": "Votre compte est désormais actif. Vous pouvez vous connecter.",
  "auth.goToLogin": "Aller à la connexion",
  "auth.sendResetLink": "Envoyer le lien de réinitialisation",
  "auth.backToLogin": "Retour à la connexion",
  "auth.newPassword": "Nouveau mot de passe",
  "auth.confirmPassword": "Confirmez le mot de passe",
  "auth.check.length": "Au moins 12 caractères",
  "auth.check.upper": "Lettre majuscule",
  "auth.check.lower": "Lettre minuscule",
  "auth.check.number": "Chiffre",
  "auth.check.match": "Les mots de passe correspondent",
  "auth.setAndActivate": "Définir le mot de passe et activer le compte",
  "auth.landing.title": "Où souhaitez-vous aller ?",
  "auth.landing.signedInAs": "Connecté en tant que {email}",
  "auth.landing.noAccess": "Vous n’avez encore accès à aucune entreprise. Demandez à un administrateur de vous ajouter.",
  "auth.landing.platform": "Administration de la plateforme",
  "auth.landing.platformSub": "Entreprises, licences, proxy de périphérie et paramètres",
  "auth.landing.companyAdmin": "Administrateur de l’entreprise",
  "auth.landing.member": "Membre",
  "auth.ssoIncomplete": "La connexion n’a pas pu aboutir. Reconnectez-vous.",
  "auth.backToSignIn": "Retour à la connexion",
  "auth.signingIn": "Connexion en cours…",
  "auth.reauth.title": "Confirmez votre identité",
  "auth.reauth.sso": "Cette modification exige que vous vous reconnectiez auprès de votre fournisseur d’identité (au plus toutes les 10 minutes). Effectuez ensuite la modification une nouvelle fois.",
  "auth.reauth.idpUnreachable": "Impossible de joindre le fournisseur d’identité",
  "auth.reauth.password": "Cette modification nécessite à nouveau votre mot de passe. Il ne vous sera plus demandé pendant les 10 prochaines minutes.",
  "auth.tf.startAgain": "← Recommencer",
  "auth.tf.setupTitle": "Configurer la connexion en deux étapes",
  "auth.tf.setupIntro": "Votre compte nécessite un code d’une application d’authentification à chaque connexion. Configurez-la une fois, maintenant.",
  "auth.tf.title": "Connexion en deux étapes",
  "auth.tf.enterRecovery": "Saisissez l’un de vos codes de récupération. Chacun ne fonctionne qu’une fois.",
  "auth.tf.enterCode": "Saisissez le code à 6 chiffres que votre application d’authentification affiche maintenant.",
  "auth.tf.recoveryCode": "Code de récupération",
  "auth.tf.code": "Code",
  "auth.tf.verify": "Vérifier",
  "auth.tf.useApp": "Utiliser un code de l’application",
  "auth.tf.lostPhone": "Téléphone perdu ? Utilisez un code de récupération",
  "auth.tf.couldNotStart": "Impossible de lancer la configuration",
  "auth.tf.savedContinue": "Je les ai enregistrés — continuer",
  "auth.tf.saved": "Je les ai enregistrés",
  "auth.tf.step1": "Ouvrez une application d’authentification sur votre téléphone (Google Authenticator, Microsoft Authenticator, 1Password, …).",
  "auth.tf.step2": "Scannez le code QR ou saisissez la clé.",
  "auth.tf.step3": "Saisissez le code à 6 chiffres affiché par l’application.",
  "auth.tf.qrAlt": "Code QR contenant la clé, à scanner avec votre application d’authentification",
  "auth.tf.key": "Clé",
  "auth.tf.codeFromApp": "Code de l’application",
  "auth.tf.turnOn": "Activer",
  "auth.tf.saveCodes": "Conservez ces codes de récupération en lieu sûr, par exemple dans un gestionnaire de mots de passe. Chacun vous permet de vous connecter une fois si vous perdez votre téléphone. Ils ne sont affichés que maintenant.",
  "auth.tf.recoveryCodes": "Codes de récupération",
  "auth.tf.codesCopied": "Codes de récupération copiés",
  "auth.tf.copyHint": "Sélectionnez les codes et copiez-les, ou téléchargez-les.",
  "auth.prot.ssoLinked": "Authentification unique associée",
  "auth.prot.ssoLinkedText": "Vous pouvez maintenant vous connecter avec votre fournisseur d’identité.",
  "auth.prot.couldNotStart": "Impossible de démarrer",
  "auth.prot.title": "Protection de la connexion",
  "auth.prot.idleLabel": "Me déconnecter après une période d’inactivité",
  "auth.prot.afterMax": "Après {duration} (le maximum autorisé ici)",
  "auth.prot.after": "Après {duration}",
  "auth.prot.never": "Jamais",
  "auth.prot.staySignedIn": "Vous restez connecté lorsque vous n’utilisez pas le tableau de bord, jusqu’à ce que la session atteigne sa durée maximale.",
  "auth.prot.signedOutAfter": "Une session laissée ouverte est déconnectée {duration} après sa dernière utilisation.",
  "auth.prot.newDeviceMail": "M’envoyer un e-mail lorsque mon compte se connecte depuis un appareil jamais utilisé auparavant",
  "auth.prot.sso": "Authentification unique",
  "auth.prot.linked": "Associé",
  "auth.prot.linkedText": "Vous pouvez vous connecter avec le fournisseur d’identité de votre entreprise.",
  "auth.prot.linkText": "Votre entreprise se connecte via son fournisseur d’identité. Associez votre compte pour l’utiliser.",
  "auth.prot.linkMine": "Associer mon compte",
  "auth.sec.isOff": "La connexion en deux étapes est désactivée",
  "auth.sec.isOn": "La connexion en deux étapes est activée",
  "auth.sec.signedOutDevice": "Déconnecté sur cet appareil",
  "auth.sec.signedOutOthers": { one: "Déconnecté sur {count} autre appareil", other: "Déconnecté sur {count} autres appareils" },
  "auth.sec.couldNotSignOut": "Impossible de se déconnecter",
  "auth.sec.newSetNote": "Un nouveau jeu remplace l’ancien : les anciens codes cessent de fonctionner.",
  "auth.sec.offNote": "La connexion ne nécessitera plus que votre mot de passe.",
  "auth.sec.codeFromYourApp": "Code de votre application d’authentification",
  "auth.sec.newCodes": "Nouveaux codes de récupération",
  "auth.sec.turnOff": "Désactiver",
  "auth.sec.on": "Activée",
  "auth.sec.since": { one: "depuis le {date} · {count} code de récupération restant", other: "depuis le {date} · {count} codes de récupération restants" },
  "auth.sec.required": " · obligatoire pour votre compte",
  "auth.sec.offText": "Désactivée. Avec la connexion en deux étapes, un mot de passe volé ne suffit pas : la connexion nécessite aussi un code d’une application sur votre téléphone.",
  "auth.sec.whereSignedIn": "Où vous êtes connecté",
  "auth.sec.thisDevice": "Cet appareil",
  "auth.sec.signedInWhen": "connecté {when}",
  "auth.sec.activeWhen": "actif {when}",
  "auth.sec.endsDate": "se termine le {date}",
  "auth.sec.signOutDevice": "Déconnecter {device}, connecté {when}",
  "auth.sec.confirmSignOutAll": "Se déconnecter sur tous les autres appareils ?",
  "auth.sec.signOutOthers": "Déconnecter les autres appareils",
  "auth.landing.client": "Événements de {customer}",
  "auth.landing.clientOpens": "Événements de {customer} — ouvert à partir du {when}",
  "auth.landing.clientNoEvent": "Événements de {customer} — ouvert au prochain événement",
  "auth.clientWindowClosed": "La connexion n’est ouverte que pendant votre événement",
  "auth.app.stayPrompt": "Rester connecté sur ce téléphone",
  "auth.app.unlockReason": "Déverrouiller Velanox",
  "auth.app.unlock": "Déverrouiller",
  "auth.app.changeServer": "Changer de serveur",
  "auth.app.offline": "Vous êtes hors ligne",
}

export default auth
